import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';

/**
 * Auth Service - Handles server-side secure HttpOnly cookie management
 * 
 * This service works with the auth-cookies Edge Function to set and clear
 * HttpOnly cookies for auth tokens, providing much stronger security
 * than client-side cookie storage.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

/**
 * Sets a secure HttpOnly cookie via the Edge Function when user signs in
 * @param token Auth token to store securely
 */
export const setSecureAuthCookie = async (token: string): Promise<boolean> => {
  try {
    logger.debug('Setting secure HttpOnly auth cookie via edge function');
    
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/auth-cookies`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'set'
        })
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      logger.error('Failed to set secure HttpOnly auth cookie', errorData);
      return false;
    }
    
    const result = await response.json();
    logger.debug('Successfully set secure HttpOnly auth cookie');
    
    // Cross-domain development helper
    if (import.meta.env.DEV || import.meta.env.MODE === 'development') {
      // This is only for development/testing - helps with cross-domain testing
      const isDev = window.location.hostname === 'localhost' || 
                    window.location.hostname.includes('127.0.0.1');
      const isNetlify = window.location.hostname.includes('netlify.app');
      
      if (isDev || isNetlify) {
        logger.debug('Development mode: Setting fallback cookie for cross-domain testing');
        // Set a regular cookie as fallback for development/testing cross-domain scenarios
        // This should NOT be relied upon in production
        document.cookie = `cross_domain_auth_token=${token}; path=/; max-age=${60*60*24*7}; ${window.location.protocol === 'https:' ? 'secure;' : ''} SameSite=Lax`;
      }
    }
    
    return true;
  } catch (error) {
    logger.error('Error setting secure HttpOnly auth cookie', error);
    return false;
  }
};

/**
 * Clears the secure HttpOnly cookie via the Edge Function when user signs out
 * @param token Current auth token needed for authorization
 */
export const clearSecureAuthCookie = async (token: string): Promise<boolean> => {
  try {
    logger.debug('Clearing secure HttpOnly auth cookie via edge function');
    
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/auth-cookies`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'clear'
        })
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      logger.error('Failed to clear secure HttpOnly auth cookie', errorData);
      return false;
    }
    
    const result = await response.json();
    logger.debug('Successfully cleared secure HttpOnly auth cookie');
    return true;
  } catch (error) {
    logger.error('Error clearing secure HttpOnly auth cookie', error);
    return false;
  }
};

/**
 * Hook into auth state changes to manage HttpOnly cookies
 * This should be called once during app initialization
 */
export const initializeSecureAuthHandling = (): void => {
  supabase.auth.onAuthStateChange(async (event, session) => {
    if (event === 'SIGNED_IN' && session?.access_token) {
      // When user signs in, set the HttpOnly cookie
      await setSecureAuthCookie(session.access_token);
    } 
    else if (event === 'SIGNED_OUT') {
      // No token available on sign-out, so we don't clear the cookie here
      // The cookie is cleared by the supabase client's sign-out process
      logger.debug('User signed out - client-side cookie handling');
    }
  });
};

export default {
  setSecureAuthCookie,
  clearSecureAuthCookie,
  initializeSecureAuthHandling
};
